import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StopClickPropagationDirective } from '@directives/stop-click-propagation/stop-click-propagation.directive';
import { AdminOverrideBannerComponent } from '@features/shopping/admin-override/admin-override-banner/admin-override-banner.component';
import { AssetSelectionItemComponent } from '@features/shopping/asset/selection-item/asset-selection-item.component';
import { AssetSelectionComponent } from '@features/shopping/asset/selection/asset-selection.component';
import { DeviceCreationFormComponent } from '@features/shopping/device-creation-form/device-creation-form.component';
import { AssetDetailsResponse } from '@models/asset-details.model';
import { DeviceAsset, LineAsset, UserAsset, UserAssetData } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  calculateItemsToDelete,
  calculateMultiSimType,
  constructAdditionalAssetTitle,
  constructAssetTitle,
  determineAssetCatalogInfo,
  hasDeviceId,
  hasMultiSim,
  removeDeviceToActivateParams
} from '@models/functions.model';
import { MultiSimModalAsset } from '@models/multi-sim.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import {
  DeviceData,
  DeviceItemDevice,
  HelpTextsInfo,
  OrderTypeSettings,
  ShoppingCategoryData,
  ShoppingDataInitialLoad,
  ShoppingFilterData,
  ShoppingFilterItem
} from '@models/shopping.model';
import { NgbAlertModule, NgbOffcanvas, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { AssetDetailsService } from '@services/asset-details/asset-details.service';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { MultisimOffcanvasComponent } from '@shared-components/multisim-offcanvas/multisim-offcanvas.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective, DomSanitizerPipe } from 'tangoe-design-system';

export const MODAL_BODY = {
  device_not_found: `Your business does not support adding a service to this device model. Please select a different device.`,
  cold_device_warning: `Your business does not support adding new services to this device as it is currently associated with an active line of service. Please select a different device.`
};

@Component({
  selector: 'ts-activity-3-device-selection',
  templateUrl: './device-selection.component.html',
  imports: [
    CommonModule,
    NgbAlertModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    DomSanitizerPipe,
    TranslationPipe,
    DataLoaderDirective,
    StopClickPropagationDirective,
    AssetSelectionComponent,
    AdminOverrideBannerComponent,
    AssetSelectionItemComponent,
    DeviceCreationFormComponent,
    MultisimOffcanvasComponent
  ]
})
export class DeviceSelectionComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('deviceIdentifierInfo') device_identifier_info_template: TemplateRef<any>;
  @ViewChild('blockFlowModal') block_flow_template: TemplateRef<any>;
  @ViewChild('multiSimDeviceModal') multi_sim_device_modal_template: TemplateRef<any>;
  @ViewChild('multiSimLineModal') multi_sim_line_modal_template: TemplateRef<any>;
  search_form: FormGroup = new FormGroup({
    device_identifier: new FormControl('')
  });
  order_type_id: number;
  admin_override: FormControl = new FormControl(null);
  modal_body: string;
  user_assets: UserAsset[] | undefined = undefined;
  search_result: UserAsset[] | undefined = undefined;
  show_search_results: boolean = false;
  shopping_cart: ShoppingCartData;
  existing_asset: UserAsset | undefined;
  carrier_id_from_cart: number | undefined;
  device_identifier_help_text: string;
  device_id_matches: boolean = false;
  device_result_initial_load: ShoppingDataInitialLoad;
  device_result_carrier_id: number | undefined;
  device_result_catalog_device: DeviceItemDevice | undefined;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  multi_sim_modal_asset: MultiSimModalAsset | undefined;
  from_chained_multi_sim_device: boolean = false;
  offcanvas_asset: UserAsset;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private activity_state_svc: ActivityStateService,
    private asset_details_svc: AssetDetailsService,
    private dashboard_svc: DashboardService,
    private modal_svc: ModalService,
    private offcanvas_svc: NgbOffcanvas,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {
    MODAL_BODY.device_not_found = this.translation_svc.translate('device_not_found');
    MODAL_BODY.cold_device_warning = this.translation_svc.translate('cold_device_warning');
  }

  ngOnInit(): void {
    this.order_type_id = this.activity_manager_svc.state().parameter('type')?.order_type_id!;

    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
          this.existing_asset = res.existing_asset;
          this.carrier_id_from_cart = res.carrier?.id;
          this.admin_override.setValue(res.device_include_admin_options);
        },
        error: (err: HttpErrorResponse) => {}
      })
    );

    this.subscriptions.add(
      this.translation_svc.getTranslatedHelpTexts(this.order_type_id).subscribe({
        next: (res: HelpTextsInfo) => {
          this.device_identifier_help_text = res.device_identifier_help_text;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.existing_asset?.asset_selection_case === 3) {
        let ele: HTMLElement | null = document.getElementById('DeviceCreationForm');
        if (ele) ele.scrollIntoView({ behavior: 'smooth' });
      }
    }, 2000);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get modal_header(): string {
    return (
      this.activity_manager_svc.state().parameter('type')?.activity_display_name ??
      this.translation_svc.translate('add_service_to_existing_device')
    );
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  getAdminOverride(admin_override: boolean): void {
    this.admin_override.setValue(admin_override);
  }

  getUserAssets(event: UserAssetData): void {
    this.user_assets = (event.assets ?? []).filter((asset: UserAsset) => Boolean(asset.device));
    this.activity_state_svc.setSteps(event.progress_bar_config.steps);

    switch (this.existing_asset?.asset_selection_case) {
      case 1: // selected via asset-list
        // Pinning for this case are done through pinCartDevice() method on ts-asset-selection
        break;
      case 2: // selected via search
        this.search_form.controls['device_identifier'].setValue(
          this.existing_asset.device_identifier_search_value
        );
        this.searchDevices(true);
        break;
      case 3: // selected via form
        this.search_form.controls['device_identifier'].setValue(
          this.existing_asset.device_identifier_search_value
        );
        this.show_search_results = true;
        break;
      default:
        break;
    }
  }

  searchResultTemplate(): string {
    if (this.show_search_results) return this.search_result ? 'assets' : 'noAssets';
    else return 'none';
  }

  searchDevices(scroll_into_view: boolean = false): void {
    if (!this.search_form.value.device_identifier) {
      this.search_result = undefined;
      this.show_search_results = false;
      return;
    }

    this.subscriptions.add(
      this.dashboard_svc
        .lookupDevice(this.search_form.value.device_identifier)
        .subscribe((res: UserAssetData) => {
          res.assets.forEach((asset: UserAsset) => {
            asset.multi_sim_type = calculateMultiSimType(asset);
          });
          this.search_result = res.assets?.length > 0 ? res.assets : undefined;

          this.pinCartDevice();

          this.device_id_matches = false;
          res.assets.forEach((asset: UserAsset) => {
            if (this.device_id_matches) return;

            this.device_id_matches = [asset.device?.id, asset.device?.device_catalog_id].includes(
              this.existing_asset?.device?.is_override
                ? this.existing_asset?.device?.existing_asset_id
                : this.existing_asset?.device?.id
            );

            if (asset?.additional_devices && !this.device_id_matches)
              asset.additional_devices.forEach((device: DeviceAsset) => {
                if (this.device_id_matches) return;

                this.device_id_matches =
                  device?.id === this.existing_asset?.device?.id ||
                  device?.device_catalog_id === this.existing_asset?.device?.device_catalog_id;
              });
          });

          this.show_search_results = true;

          if (scroll_into_view)
            setTimeout(() => {
              document
                .getElementById('DeviceIdentifierSearchResults')
                ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 200);
        })
    );
  }

  isSelectedAsset(asset: UserAsset): boolean | undefined {
    let additional_devices_match: boolean = false;
    let additional_lines_match: boolean = false;

    if (asset?.additional_devices)
      additional_devices_match = asset.additional_devices.find(
        (device: DeviceAsset) =>
          device?.id === this.existing_asset?.device?.id ||
          device?.id === this.existing_asset?.device?.existing_asset_id
      )
        ? true
        : false;

    if (asset?.additional_lines)
      additional_lines_match = asset.additional_lines.find(
        (line: LineAsset) => line?.id === this.existing_asset?.line?.id
      )
        ? true
        : false;

    return ((this.existing_asset &&
      asset.device &&
      asset.device.id &&
      asset.multi_sim_type === this.existing_asset?.multi_sim_type &&
      (asset.device.id == this.existing_asset?.device?.id || additional_devices_match)) ||
      (asset.line &&
        asset.line.id &&
        asset?.multi_sim_type === this.existing_asset?.multi_sim_type &&
        (asset.line.id == this.existing_asset?.line?.id || additional_lines_match)) ||
      (this.existing_asset?.device?.is_override &&
        asset.device.id === this.existing_asset?.device?.existing_asset_id)) as boolean;
  }

  openDeviceIdentifierInfoModal(event: MouseEvent): void {
    event.preventDefault();
    this.modal_svc.open(this.device_identifier_info_template, {
      backdrop: 'static',
      centered: true,
      size: 'lg'
    });
  }

  assetAriaLabel(asset: UserAsset): string {
    return constructAssetTitle(asset);
  }

  dismissModal(): void {
    this.modal_svc.dismissAll();
  }

  selectAssetClick(
    asset: UserAsset | DeviceAsset,
    selection_case: number,
    from_modal: boolean
  ): void {
    if (!from_modal) {
      this.from_chained_multi_sim_device = false;
      this.multi_sim_modal_asset = undefined;
    }

    this.shopping_svc.getPlanSettings(this.order_type_id).subscribe({
      next: (res: OrderTypeSettings) => {
        if (res.require_cold_device && (asset as UserAsset)?.line?.status === 'Active')
          this.openColdDeviceWarningModal();
        else {
          if (!from_modal) {
            this.multi_sim_modal_asset = {
              asset: asset as UserAsset,
              selection_case: selection_case
            };

            if (
              [
                this.multi_sim_types[0],
                this.multi_sim_types[2],
                this.multi_sim_types[3],
                this.multi_sim_types[5]
              ].includes(this.multi_sim_modal_asset.asset!.multi_sim_type)
            )
              this.getAssetCatalogInfo(
                this.multi_sim_modal_asset?.asset?.device,
                selection_case,
                false
              );
            else
              this.modal_svc.open(this.multi_sim_line_modal_template, {
                backdrop: 'static',
                centered: true
              });
          } else {
            (document.activeElement as HTMLElement).blur();
            this.modal_svc.dismissAll();
            this.getAssetCatalogInfo(
              asset as DeviceAsset,
              selection_case,
              false,
              res.require_cold_device
            );
          }
        }
      },
      error: () => {}
    });
  }

  selectMultiSimLine(asset: LineAsset, add_new_line: boolean = false): void {
    this.multi_sim_modal_asset! = {
      asset: {
        ...this.multi_sim_modal_asset!.asset,
        line: asset
      },
      add_additional_line: add_new_line,
      selection_case: this.multi_sim_modal_asset?.selection_case!
    };

    this.modal_svc.dismissAll();

    this.getAssetCatalogInfo(
      this.multi_sim_modal_asset.asset.device,
      this.multi_sim_modal_asset.selection_case,
      true
    );
  }

  constructAssetTitle(
    asset: UserAsset | undefined,
    include_details: boolean = false
  ): string | undefined {
    if (!asset) return;

    return constructAssetTitle(asset, include_details);
  }

  constructAdditionalAssetTitle(asset: DeviceAsset | LineAsset, type: number): string {
    return constructAdditionalAssetTitle(asset, type);
  }

  calculateModalAdditionalLines(asset: UserAsset | undefined): LineAsset[] | undefined {
    if (!asset || !asset.additional_lines) return;

    return asset.additional_lines;
  }

  calculateModalAdditionalDevices(asset: UserAsset | undefined): DeviceAsset[] | undefined {
    if (!asset || !asset.additional_devices) return;

    return asset.additional_devices?.filter((device: DeviceAsset) => hasDeviceId(device));
  }

  multisimModalBody(asset: UserAsset | undefined): string | undefined {
    if (!asset) return;

    return [this.multi_sim_types[0], this.multi_sim_types[2]].includes(asset.multi_sim_type) ||
      this.from_chained_multi_sim_device
      ? this.translation_svc.translate('multisim_add_replace_line_prompt')
      : this.translation_svc.translate('multisim_line_number_is', asset.line.international_number);
  }

  multisimModalPrompt(asset: UserAsset | undefined): string | undefined {
    if (!asset) return;

    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_select_eligible_line')
      : this.translation_svc.translate('multisim_select_eligible_device');
  }

  hasDeviceId(device: DeviceAsset): boolean {
    return hasDeviceId(device);
  }

  lineInCart(line: LineAsset): boolean {
    return (
      line?.id === this.shopping_cart.existing_asset?.line?.id &&
      this.existing_asset?.multi_sim_type === this.multi_sim_modal_asset?.asset.multi_sim_type
    );
  }

  deviceInCart(device: DeviceAsset | undefined): boolean {
    if (!device) return false;

    return (
      (device?.id === this.shopping_cart.existing_asset?.device?.id ||
        device?.id === this.shopping_cart.existing_asset?.device?.existing_asset_id) &&
      this.existing_asset?.multi_sim_type === this.multi_sim_modal_asset?.asset.multi_sim_type
    );
  }

  hasMultiSim(asset: UserAsset): boolean {
    return hasMultiSim(asset);
  }

  openAssetOffCanvas(content: TemplateRef<any>, asset: UserAsset): void {
    this.offcanvas_asset = asset;

    this.offcanvas_svc.open(content, {
      position: 'end',
      panelClass: 'multisim-offcanvas',
      backdrop: 'static'
    });
  }

  cancelAdminOverrideToggle(cancel_override: boolean): void {
    if (cancel_override)
      this.admin_override.setValue(this.shopping_cart.device_include_admin_options);
  }

  private getAssetCatalogInfo(
    asset: DeviceAsset,
    selection_case: number,
    from_lines_modal: boolean,
    req_cold_device: boolean = false
  ): void {
    this.device_result_initial_load = {
      categories: [],
      filters: [],
      orderTypeId: this.order_type_id,
      deviceId: asset.device_catalog_id!
    };

    this.shopping_svc.getDevices(this.device_result_initial_load).subscribe({
      next: (data: DeviceData) => {
        let filters: ShoppingFilterData[] = data.filters;
        filters.forEach((filter: ShoppingFilterData) => {
          let shopping_filter_item: ShoppingFilterItem;
          switch (filter.config.filter_name) {
            case 'manufacturer':
              shopping_filter_item = {
                id: filter.config.filter_config_id,
                value: asset.manufacturer,
                filter_name: filter.config.filter_name
              };
              this.device_result_initial_load.filters.push(shopping_filter_item);
              break;
            case 'model':
              shopping_filter_item = {
                id: filter.config.filter_config_id,
                value: asset.model,
                filter_name: filter.config.filter_name
              };
              this.device_result_initial_load.filters.push(shopping_filter_item);
              break;
            case 'color':
              shopping_filter_item = {
                id: filter.config.filter_config_id,
                value: asset.normalized_color,
                filter_name: filter.config.filter_name
              };
              this.device_result_initial_load.filters.push(shopping_filter_item);
              break;
            case 'size':
              if (asset.size) {
                shopping_filter_item = {
                  id: filter.config.filter_config_id,
                  value: asset.size,
                  filter_name: filter.config.filter_name
                };
                this.device_result_initial_load.filters.push(shopping_filter_item);
              }
              break;
            default:
              break;
          }
        });

        let categories: ShoppingCategoryData[] = data.category;
        categories.forEach((category: ShoppingCategoryData) => {
          if (category.config.filter_name === 'product_category') {
            this.device_result_initial_load.categories = [
              {
                id: category.config.filter_config_id,
                value: 'All'
              }
            ];
          }
        });

        this.shopping_svc.getDevices(this.device_result_initial_load).subscribe({
          next: (data: DeviceData) => {
            [this.device_result_carrier_id, this.device_result_catalog_device] =
              this.determineDeviceCatalogInfo(asset, data.items);

            if (!this.device_result_carrier_id || !this.device_result_catalog_device) {
              this.multi_sim_modal_asset = undefined;
              this.modal_svc.dismissAll();

              this.modal_body = MODAL_BODY['device_not_found'];
              this.modal_svc.open(this.block_flow_template, {
                backdrop: 'static',
                centered: true
              });
              return;
            } else if (from_lines_modal) {
              if (this.showConfirmChangesModal(this.multi_sim_modal_asset?.asset!))
                this.confirmChangesModal(() =>
                  this.selectAsset(this.multi_sim_modal_asset!.asset, selection_case, true)
                );
              else
                this.selectAsset(
                  this.multi_sim_modal_asset!.asset,
                  this.multi_sim_modal_asset!.selection_case
                );
            } else {
              if (
                ![
                  this.multi_sim_types[1],
                  this.multi_sim_types[3],
                  this.multi_sim_types[6]
                ].includes(this.multi_sim_modal_asset?.asset?.multi_sim_type!)
              )
                this.modal_svc.open(this.multi_sim_device_modal_template, {
                  backdrop: 'static',
                  centered: true
                });
              else this.getChainedAssets(asset as DeviceAsset, selection_case, req_cold_device);
            }
          },
          error: () => {}
        });
      }
    });
  }

  private getChainedAssets(
    asset: DeviceAsset,
    selection_case: number,
    req_cold_device: boolean = false
  ): void {
    if (selection_case === 2)
      this.subscriptions.add(
        this.dashboard_svc
          .lookupDevice(
            asset?.imei ??
              asset?.meid ??
              asset?.esn ??
              asset?.serial_number ??
              asset?.msn ??
              this.search_form.value.device_identifier,
            true
          )
          .subscribe({
            next: (res: UserAssetData) => {
              let has_active_line: boolean =
                res.assets[0]?.line?.status === 'Active' ? true : false;
              res.assets[0]?.additional_lines?.forEach((line: LineAsset) => {
                if (line?.status === 'Active') has_active_line = true;
              });

              if (req_cold_device && has_active_line) this.openColdDeviceWarningModal();
              else this.chainedAssetModalLogic(res, asset, selection_case);
            },
            error: (err: HttpErrorResponse) => console.log(err)
          })
      );
    else
      this.subscriptions.add(
        this.asset_details_svc.deviceDetails((asset as DeviceAsset).id!).subscribe({
          next: (res: AssetDetailsResponse) => {
            let has_active_line: boolean = res.asset?.line?.status === 'Active' ? true : false;
            res.asset?.additional_lines?.forEach((line: LineAsset) => {
              if (line?.status === 'Active') has_active_line = true;
            });

            if (req_cold_device && has_active_line) this.openColdDeviceWarningModal();
            else this.chainedAssetModalLogic(res, asset, selection_case);
          },
          error: (err: HttpErrorResponse) => console.log(err)
        })
      );
  }

  private chainedAssetModalLogic(
    res: AssetDetailsResponse | UserAssetData,
    asset: DeviceAsset,
    selection_case: number
  ): void {
    if (
      this.multi_sim_modal_asset &&
      [this.multi_sim_types[1], this.multi_sim_types[6]].includes(
        this.multi_sim_modal_asset?.asset?.multi_sim_type
      )
    ) {
      this.multi_sim_modal_asset = {
        asset: {
          ...this.multi_sim_modal_asset.asset,
          device: asset as DeviceAsset
        },
        selection_case: this.multi_sim_modal_asset.selection_case,
        chained_multi_sim_asset:
          selection_case === 2 && (res as UserAssetData)?.assets?.length > 0
            ? (res as UserAssetData).assets[0]
            : (res as AssetDetailsResponse).asset
      };

      if ((res as AssetDetailsResponse)?.asset?.line || (res as UserAssetData)?.assets[0]?.line)
        this.from_chained_multi_sim_device = true;
    }

    if (this.from_chained_multi_sim_device) {
      this.modal_svc.dismissAll();
      this.modal_svc.open(this.multi_sim_device_modal_template, {
        backdrop: 'static',
        centered: true
      });
    } else {
      this.modal_svc.dismissAll();
      this.from_chained_multi_sim_device = false;
      if (this.showConfirmChangesModal(this.multi_sim_modal_asset?.asset!))
        this.confirmChangesModal(() =>
          this.selectAsset(this.multi_sim_modal_asset!.asset, selection_case, true)
        );
      else this.selectAsset(this.multi_sim_modal_asset?.asset!, selection_case);
    }
  }

  private showConfirmChangesModal(asset: UserAsset): boolean {
    return this.existing_asset &&
      ((this.existing_asset.device.existing_asset_id ?? this.existing_asset.device.id) !==
        asset.device.id ||
        this.existing_asset?.line?.id !== asset?.line?.id) &&
      this.shopping_cart.shopping_cart_items.length > 0
      ? true
      : false;

    // # TODO: replace return statement with the below when it's required to show the confirm clear cart modal when selecting the "Add Additional Line" option.
    // return this.existing_asset &&
    //   ((this.existing_asset.device.existing_asset_id ?? this.existing_asset.device.id) !==
    //     asset.device.id ||
    //     this.existing_asset?.line?.id !== asset?.line?.id ||
    //     this.multi_sim_modal_asset?.add_additional_line) &&
    //   this.shopping_cart.shopping_cart_items.length > 0
    //   ? true
    //   : false;
  }

  private selectAsset(asset: UserAsset, selection_case: number, clear_cart: boolean = false): void {
    asset.device = {
      ...asset.device,
      is_override:
        selection_case === this.existing_asset?.asset_selection_case
          ? this.existing_asset?.device.is_override
          : false,
      in_cart: this.existing_asset?.device.in_cart
    };
    this.existing_asset = asset;

    if (clear_cart)
      this.subscriptions.add(
        this.shopping_svc
          .clearSpecifiedCartItems(
            calculateItemsToDelete(this.shopping_cart.shopping_cart_items),
            [3].includes(this.order_type_id) ? true : false
          )
          .subscribe({
            next: (res: ShoppingCartData) => {
              this.shopping_cart = {
                ...removeDeviceToActivateParams(res),
                existing_asset: undefined
              };
              this.continueAssetSelection(
                asset,
                this.device_result_initial_load,
                [3].includes(this.order_type_id)
                  ? (this.multi_sim_modal_asset?.asset?.line?.carrier_id ??
                      this.device_result_carrier_id!)
                  : (this.carrier_id_from_cart ?? this.device_result_carrier_id!),
                this.device_result_catalog_device!,
                selection_case,
                this.search_form.controls['device_identifier'].value
              );
            },
            error: (err: HttpErrorResponse) => {}
          })
      );
    else
      this.continueAssetSelection(
        this.existing_asset ?? asset,
        this.device_result_initial_load,
        this.carrier_id_from_cart ??
          this.multi_sim_modal_asset?.asset?.line?.carrier_id ??
          this.device_result_carrier_id!,
        this.device_result_catalog_device!,
        selection_case,
        this.search_form.controls['device_identifier'].value
      );
  }

  private continueAssetSelection(
    asset: UserAsset,
    initial_load: ShoppingDataInitialLoad,
    carrier_id: number,
    catalog_device: DeviceItemDevice,
    selection_case: number,
    identifier_value: string
  ): void {
    if (this.existing_asset?.device.id && asset.device.id != this.existing_asset?.device.id)
      this.shopping_cart.device_include_admin_options =
        this.shopping_cart.accessories_include_admin_options = undefined;

    if (
      this.shopping_cart?.existing_asset &&
      this.shopping_cart.existing_asset?.device.existing_asset_id
    ) {
      asset.device.id = this.shopping_cart.existing_asset.device.id;
      asset.device.existing_asset_id = this.shopping_cart.existing_asset?.device.existing_asset_id;
    }

    const cart_payload: ShoppingCartData = {
      ...this.shopping_cart,
      country_id: this.activity_manager_svc.state().parameter('country')?.id,
      carrier_id: carrier_id,
      orderTypeId: this.order_type_id,
      lineId: asset?.line?.id,
      device_include_admin_options: this.admin_override.value,
      existing_asset: {
        ...asset,
        asset_from_inventory: true,
        asset_selection_case: selection_case,
        device_identifier_search_value: identifier_value
      },
      add_additional_line: this.multi_sim_modal_asset?.add_additional_line ?? false,
      progress_stepper: this.activity_manager_svc
        .state()
        .currentStep(this.activity_manager_svc.state().activeStepIndex())
    };

    this.shopping_svc.sendShoppingItems(cart_payload).subscribe({
      next: (res: ShoppingCartData) => {
        this.activity_manager_svc
          .state()
          .setParameter('device_include_admin_options', this.admin_override.value);
        this.dismissModal();
        this.activity_manager_svc.navigateToNextStep({
          queryParams: {
            carrierId: carrier_id,
            deviceId: catalog_device!.device_catalog_id!,
            categories: JSON.stringify(initial_load.categories),
            filters: JSON.stringify(initial_load.filters)
          }
        });
      },
      error: (error: HttpErrorResponse) => {}
    });
  }

  private openColdDeviceWarningModal(): void {
    this.modal_body = MODAL_BODY['cold_device_warning'];
    this.modal_svc.open(this.block_flow_template, {
      backdrop: 'static',
      centered: true
    });
  }

  private confirmChangesModal(callback: () => void): void {
    let modal_strings: string[] = this.translation_svc.getTranslatedModalLabels();

    this.modal_svc.confirmationDialog(
      {
        title: modal_strings[0],
        message: modal_strings[1],
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: this.translation_svc.translate('cancel'),
        onConfirm: () => {
          callback();
          this.dismissModal();
        }
      },
      {
        size: 'dialog-centered',
        centered: true,
        backdrop: 'static'
      }
    );
  }

  private determineDeviceCatalogInfo(
    asset_device: UserAsset['device'],
    device_data: DeviceData['items']
  ): [number | undefined, DeviceItemDevice | undefined] {
    return determineAssetCatalogInfo(asset_device, device_data);
  }

  private pinCartDevice(): void {
    if (this.existing_asset && this.search_result)
      this.search_result.forEach((asset: UserAsset, index: number = 0) => {
        let in_cart: boolean = false;

        if (this.isSelectedAsset(asset)) in_cart = true;

        if (in_cart && this.search_result) {
          this.search_result[index][asset.device ? 'device' : 'line'].in_cart = true;
          this.search_result?.unshift(this.search_result.splice(index, 1)[0]);
          return;
        }

        index++;
      });
  }
}
